import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { AssociationType } from "@/shared/domain/association";

import { TopicTimeBounds } from "../TopicRecord";

/**
 * The time span one file version's topics cover, without listing them.
 * {@link useDatasetTopicTimeBounds}'s counterpart for a single file, keyed by
 * version like the file's own topic query — a reingest publishes a new version
 * with different bounds.
 */
export function useFileTopicTimeBounds(
  fileId: string,
  fileVersion: number,
  options?: { isEnabled?: boolean },
) {
  const { topics: topicService } = useDomainServices();
  const { isEnabled = true } = options ?? {};

  return useQuery<TopicTimeBounds, Error>({
    queryKey: ["fileTopicTimeBounds", fileId, `fileVersion=${fileVersion}`],
    queryFn: ({ signal }) =>
      topicService.getTimeBoundsForAssociation(
        {
          association_id: fileId,
          association_type: AssociationType.File,
          association_version: fileVersion,
        },
        { abortSignal: signal },
      ),
    enabled: isEnabled,
    // Bounds move only on reingest, so they age at the same rate the topic
    // records themselves do.
    staleTime: 300000, // 5 minutes, in milliseconds
  });
}
